/**
 * The Hour Optimizer's Config tab (user, 12/10/2026: "clean that now it has
 * a bit space"): two setting cards, side by side when the tab is wide
 * enough — Extra drops and Space between — each with its control, a small
 * picture that moves with the number, and one sentence saying it with the
 * number. When the container's drops leave less room than Space between
 * asks, its card says so — no extra can fit — and offers the value that does.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode, useMemo } from 'react';
import { AlertTriangle, type LucideIcon, Timer, Zap } from 'lucide-react';

import { HintDot } from '@/components/ui';

import type { ContainerEditor } from '../../state/useContainerEditor';
import { NumberStepper } from '../controls/NumberStepper';
import { type ExtrasSpan, largestRoom } from '../hour/extras/extras-preview';
import { ExtrasPicture, SpacingPicture } from './HourConfigPictures';

const EXTRAS_HINT =
  "Extra drops use the container's default amount. The test drop goes to the busiest hour that is not ranked yet, inside its hours, when there is room — that is how the optimizer learns new hours.";
const SPACING_HINT = "Counted from every drop of this container — its own drops and the other extras. An hour without that much room gets no extra.";

const Card: FC<{ icon: LucideIcon; title: string; hint: string; children: ReactNode }> = ({ icon: Icon, title, hint, children }) => (
  <section aria-label={title} className="flex min-w-0 flex-col gap-3 rounded-lg border border-border/60 bg-card p-4 shadow-sm">
    <h3 className="flex items-center gap-2 text-[13px] font-medium">
      <Icon className="h-4 w-4 text-primary" aria-hidden />
      {title}
      <HintDot label={hint} />
    </h3>
    {children}
  </section>
);

const Sentence: FC<{ children: ReactNode }> = ({ children }) => <p className="text-xs leading-relaxed text-muted-foreground">{children}</p>;

function extrasSentence(n: number, master: boolean): string {
  const where = master ? "inside each container's hours" : "inside this container's hours";
  if (n === 0) return "No extra drops in the best hours — only the test drop, in an hour it hasn't learned yet.";
  return `Up to ${n} extra drop${n === 1 ? '' : 's'} a day, one in each of the best-ranked hours ${where} — plus the test drop in an hour it hasn't learned yet.`;
}

/** No extra can fit: even the freest minute of its day is closer than Space between to a drop. */
const NoRoom: FC<{ room: number; onUse: (minutes: number) => void }> = ({ room, onUse }) => (
  <div role="status" className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md bg-warning/10 px-2.5 py-1.5 text-xs duration-300 animate-in fade-in-0 motion-reduce:animate-none">
    <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
    <span>{room === 0 ? 'Its drops leave no free minute — no extra fits.' : `Its drops leave at most ${room} min of room — no extra fits.`}</span>
    {room >= 1 ? (
      <button type="button" onClick={() => onUse(room)} className="ml-auto rounded-md border border-warning/40 bg-background px-2 py-0.5 font-medium transition-colors hover:bg-warning/10">
        Use {room} min
      </button>
    ) : null}
  </div>
);

export const HourConfigRows: FC<{ editor: ContainerEditor; span: ExtrasSpan | null }> = ({ editor, span }) => {
  const { draft, setHourOptimizer } = editor;
  const config = draft.hourOptimizer;
  const room = useMemo(() => (span ? largestRoom(span) : null), [span]);
  const noRoom = config.maxExtraSlots > 0 && room !== null && room < config.minIntervalMin;
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(19rem,1fr))] gap-3">
      <Card icon={Zap} title="Extra drops" hint={EXTRAS_HINT}>
        <NumberStepper value={config.maxExtraSlots} onChange={(v) => setHourOptimizer({ maxExtraSlots: v })} min={0} max={12} step={1} unit="drops" presets={[0, 2, 4, 6]} ariaLabel="Most extra drops a day" />
        <ExtrasPicture count={config.maxExtraSlots} />
        <Sentence>{extrasSentence(config.maxExtraSlots, draft.kind === 'MASTER')}</Sentence>
      </Card>
      <Card icon={Timer} title="Space between" hint={SPACING_HINT}>
        <NumberStepper value={config.minIntervalMin} onChange={(v) => setHourOptimizer({ minIntervalMin: v })} min={1} max={1440} step={5} presets={[10, 20, 30]} ariaLabel="Minutes an extra drop keeps from any other drop" />
        <SpacingPicture minutes={config.minIntervalMin} />
        <Sentence>{`An extra drop keeps at least ${config.minIntervalMin.toLocaleString('en-US')} min from every other drop.`}</Sentence>
        {noRoom ? <NoRoom room={room} onUse={(minIntervalMin) => setHourOptimizer({ minIntervalMin })} /> : null}
      </Card>
    </div>
  );
};
